import {
  Directive,
  ElementRef,
  input,
  inject,
  afterNextRender,
  DestroyRef
} from '@angular/core';
import {
  Location,
  LocationStrategy,
  HashLocationStrategy
} from '@angular/common';
import { SubscriptionLike } from 'rxjs';
import type { InputSignal } from '@angular/core';
import type { PopStateEvent } from '@angular/common';

/**
 * Anchor Scroll Directive
 *
 * Allows an element to be scrolled into view when location changes
 * and the new url contains a hash that matches the component's id
 *
 * @example
 * <div tdWbAnchorScroll id="some-hash"></div>
 *
 * Then on the anchor tag use <a href="my-page#some-hash">
 */
@Directive({
  selector: '[tdWbAnchorScroll]',
  providers: [Location, { provide: LocationStrategy, useClass: HashLocationStrategy }]
})
export class AnchorScrollDirective {
  id: InputSignal<string | undefined> =  input<string>();

  private readonly FILTER_TYPE: string = 'hashchange';
  private timer: NodeJS.Timeout;
  private subscription: SubscriptionLike;

  private readonly location: Location = inject(Location);
  private readonly element: ElementRef = inject(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const path = this.location.path(false);

      // In case the page opens and already contains a location hash
      if (path.indexOf('#') !== -1 && path.endsWith(this.id())) {
        this.timer = setTimeout((): void => {
          this.element.nativeElement.scrollIntoView();
          this.timer = undefined;
        }, 0);
      }

      // and also subscribe for any sub-sequential state change event
      this.subscription = this.location.subscribe(
        (state: PopStateEvent): void => {
          if (state.type === this.FILTER_TYPE && ((state?.url?.endsWith(this.id())) ?? false)) {
            this.element.nativeElement.scrollIntoView();
          }
        }
      );
    });

    this.destroyRef.onDestroy(() => {
      if (this.timer !== undefined) {
        clearTimeout(this.timer);
      }
      if (this.subscription !== undefined) {
        this.subscription.unsubscribe();
      }
    });
  }
}
